'use client';

import React, { useImperativeHandle } from 'react';
import RobotMessageImage from './robot-message-image';
import Avatar from '@mui/material/Avatar';
import {
    GenerateImageModelState,
    MessageProps,
    RobotMessageAnimationHandle,
} from './chat-demo-type-defs';
import gsap from 'gsap';
import TextPlugin from 'gsap/TextPlugin';
import { useTheme } from '@mui/material/styles';
import { getStaticAssetUrl } from '@/app/lib/utils/static-asset-url';

gsap.registerPlugin(TextPlugin);

const RobotMessage: React.ForwardRefRenderFunction<
    RobotMessageAnimationHandle,
    { message: MessageProps }
> = ({ message }, ref) => {
    const rootRef = React.useRef<HTMLDivElement>(null);
    const infos = React.useRef<HTMLDivElement>(null);
    const images = React.useRef<HTMLDivElement>(null);
    const theme = useTheme();

    const { generateImages, generateImageConfig, modelName, sender, content } =
        message;

    const promptConfigs = generateImageConfig
        ? `${generateImageConfig.width}x${generateImageConfig.height}, ${generateImageConfig.steps}, ${generateImageConfig.cfgScale}, ${generateImageConfig.imageCount}`
        : '512x512, 50, 7, 1';

    const getAnimation = () => {
        const tl = gsap.timeline();
        tl.fromTo(
            rootRef.current,
            { opacity: 0, yPercent: 30 },
            {
                opacity: 1,
                yPercent: 0,
                duration: 1,
                onStart: () => {
                    // 隐藏图片容器（GSAP 独占，不经过 React state）
                    if (images.current) {
                        gsap.set(images.current, {
                            display: 'none',
                            filter: 'blur(20px)',
                        });
                    }
                },
            },
        );
        tl.to(infos.current, {
            duration: 0,
            text: '(generating ...)',
        });

        // 先显示容器，再做 blur 淡入
        tl.fromTo(
            images.current,
            { filter: 'blur(20px)' },
            {
                filter: 'blur(0px)',
                duration: 5,
                ease: 'power2.out',
                onStart: () => {
                    if (images.current) {
                        gsap.set(images.current, { display: 'grid' });
                    }
                },
            },
            '<',
        );

        tl.to(
            infos.current,
            {
                duration: 0,
                delay: 2,
                text: '(Hover over image for actions 👇)',
            },
            '<',
        );
        return tl;
    };

    useImperativeHandle(ref, () => ({
        getAnimation,
    }));

    return (
        <div
            ref={rootRef}
            className="tablet:px-[16px] flex h-full w-full grow  flex-row px-[8px] text-[#06060A]"
        >
            {/*头像*/}
            <div className="mt-[8px] flex h-full justify-center">
                <Avatar
                    sx={{
                        width: 30,
                        height: 30,
                        [theme.breakpoints.up(735)]: {
                            width: 40,
                            height: 40,
                        },
                    }}
                    alt="robot"
                    src={getStaticAssetUrl('/model_foresight.webp')}
                    slotProps={{
                        img: {
                            onError: (
                                e: React.SyntheticEvent<HTMLImageElement>,
                            ) => {
                                (e.target as HTMLImageElement).style.display =
                                    'none';
                            },
                        },
                    }}
                />
            </div>

            {/*消息*/}
            <div className="tablet:ml-[12px] ml-[8px] mt-[8px] flex h-full min-w-0 flex-1 flex-col">
                {/*模型名称 + 时间戳*/}
                <div className="j-l2 flex-none font-medium text-[#06060A]">
                    {modelName}
                </div>

                {/*回复内容*/}
                <div className="mt-[4px] flex min-h-0 w-full grow flex-col">
                    {/*回复用户消息说明*/}
                    <div className="j-t4 flex w-full flex-none flex-wrap items-center">
                        <div className="inline-flex gap-[2px] whitespace-nowrap rounded-[4px] bg-[#6c5df729] px-[3px] py-[1px] text-[#6C5DF7]">
                            @
                            <Avatar
                                sx={{ width: 18, height: 18 }}
                                alt="kevin"
                                src={getStaticAssetUrl(
                                    '/simulaix/kevin-avatar.png',
                                )}
                                slotProps={{
                                    img: {
                                        crossOrigin: 'anonymous',
                                        onError: (
                                            e: React.SyntheticEvent<HTMLImageElement>,
                                        ) => {
                                            (
                                                e.target as HTMLImageElement
                                            ).style.display = 'none';
                                        },
                                    },
                                }}
                            />
                            {sender}
                        </div>
                        <div className="ml-[4px] text-[#515151]">{content}</div>
                    </div>
                    {/*重新生成*/}
                    <div ref={infos} className="j-t4 my-[5px] flex-none">
                        (preparing to start)
                    </div>

                    <div
                        ref={images}
                        className="tablet:grid-cols-4 grid min-h-0 w-full grow grid-cols-2 gap-[8px] overflow-hidden [grid-auto-rows:1fr]"
                        style={{ display: 'none' }}
                    >
                        {generateImages && Array.isArray(generateImages)
                            ? Object.entries(
                                  generateImages.reduce(
                                      (acc, image) => {
                                          if (!acc[image.batchIndex]) {
                                              acc[image.batchIndex] = [];
                                          }

                                          acc[image.batchIndex].push(image);
                                          return acc;
                                      },
                                      {} as {
                                          [
                                              key: string
                                          ]: GenerateImageModelState[];
                                      },
                                  ),
                              ).map(([batchIndex, singleBatchImages]) => {
                                  return (
                                      <RobotMessageImage
                                          key={batchIndex}
                                          batchIndex={batchIndex}
                                          batchImages={singleBatchImages}
                                          sourceImageWidth={
                                              generateImageConfig?.width || 512
                                          }
                                          sourceImageHeight={
                                              generateImageConfig?.height || 512
                                          }
                                      />
                                  );
                              })
                            : null}
                    </div>
                    {/*bs*/}
                    <div className="j-t4 mt-[4px] flex-none text-[#878789]">
                        {`(Bs : ${promptConfigs})`}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default React.memo(React.forwardRef(RobotMessage));
